import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, deleteUser } from 'firebase/auth';
import { firestore, auth } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';

const Settings = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Basic notification settings
  const [settings, setSettings] = useState({
    emailNotifications: true,
    jobAlerts: true,
    messageNotifications: true
  });

  // Account settings
  const [accountForm, setAccountForm] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (currentUser) {
        try {
          // Load notification settings
          const userDoc = await getDoc(doc(firestore, 'userSettings', currentUser.uid));
          if (userDoc.exists()) {
            setSettings({ ...settings, ...userDoc.data() });
          }
          
          // Load account info
          const profileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
          let phoneNumber = '';
          if (profileDoc.exists()) {
            phoneNumber = profileDoc.data().phoneNumber || '';
          }
          
          setAccountForm({
            email: currentUser.email || '',
            phoneNumber: phoneNumber,
            password: '',
            confirmPassword: ''
          });
        } catch (error) {
          console.error('Error fetching settings:', error);
        }
      }
      setLoading(false);
    };

    fetchSettings();
  }, [currentUser]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAccountInputChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveNotificationSettings = async () => {
    if (!currentUser) return;
    
    setSaving(true);
    try {
      await setDoc(doc(firestore, 'userSettings', currentUser.uid), settings, { merge: true });
      setMessage('Notification settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Error saving settings. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    }
    setSaving(false);
  };

  const saveAccountSettings = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    if (accountForm.password !== accountForm.confirmPassword) {
      setMessage('Passwords do not match.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setSaving(true);
    try {
      // Update password if provided
      if (accountForm.password) {
        await updatePassword(auth.currentUser, accountForm.password);
      }
      
      // Update phone in Firestore if changed
      await setDoc(doc(firestore, 'userProfiles', currentUser.uid), {
        phoneNumber: accountForm.phoneNumber,
      }, { merge: true });
      
      setMessage('Account settings saved successfully!');
      // Clear password fields
      setAccountForm(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        setMessage('For security, please log out and log in again before changing your password.');
      } else {
        setMessage('Failed to update account: ' + (error.message || 'Unknown error'));
      }
      setTimeout(() => setMessage(''), 3000);
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    setDeleting(true);
    try {
      // Delete user data from Firestore
      await deleteDoc(doc(firestore, 'userProfiles', currentUser.uid));
      await deleteDoc(doc(firestore, 'userSettings', currentUser.uid));
      
      // Delete the user account from Firebase Auth
      await deleteUser(auth.currentUser);
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        setMessage('For security, please log out and log in again before deleting your account.');
      } else {
        setMessage('Failed to delete account: ' + (error.message || 'Unknown error'));
      }
      setTimeout(() => setMessage(''), 3000);
      setDeleting(false);
    }
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account and notification preferences</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="px-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'notifications'
                    ? 'border-[#FF8C00] text-[#FF8C00]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'account'
                    ? 'border-[#FF8C00] text-[#FF8C00]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Account Security
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-gray-900">Email Notifications</h3>
                      <p className="text-sm text-gray-500 text-left">Receive email updates about your applications</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF8C00]/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF8C00]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-gray-900">Job Alerts</h3>
                      <p className="text-sm text-gray-500 text-left">Get notified about new job opportunities</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.jobAlerts}
                        onChange={(e) => handleSettingChange('jobAlerts', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF8C00]/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF8C00]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-gray-900">Message Notifications</h3>
                      <p className="text-sm text-gray-500 text-left">Receive notifications for new messages</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.messageNotifications}
                        onChange={(e) => handleSettingChange('messageNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF8C00]/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF8C00]"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  onClick={saveNotificationSettings}
                  disabled={saving}
                  className="px-6 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Notifications'}
                </button>
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <form onSubmit={saveAccountSettings} className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Security</h2>
                  <p className="text-sm text-gray-500 mb-6">Update your login credentials and contact information</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={accountForm.email}
                        disabled
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-500 cursor-not-allowed"
                        title="Email address cannot be changed"
                      />
                      <p className="text-xs text-gray-500 mt-1">Email address cannot be changed for security reasons</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={accountForm.phoneNumber}
                        onChange={handleAccountInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        name="password"
                        value={accountForm.password}
                        onChange={handleAccountInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                        placeholder="Leave blank to keep current password"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={accountForm.confirmPassword}
                        onChange={handleAccountInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Account Settings'}
                  </button>
                </div>
              </form>

              {/* Danger Zone */}
              <div className="pt-6 border-t border-gray-200">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Danger Zone</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success/Error Messages */}
          {message && (
            <div className={`mt-4 p-3 rounded-md text-sm ${
              message.includes('Error') || message.includes('Failed') || message.includes('not match')
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Account</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete your account? This action cannot be undone. All your data, including:
            </p>
            <ul className="text-sm text-gray-600 mb-4 list-disc list-inside">
              <li>Your profile information</li>
              <li>Job applications</li>
              <li>Messages and conversations</li>
              <li>Notification preferences</li>
            </ul>
            <p className="text-sm text-gray-600 mb-6">will be permanently deleted.</p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 